'use client';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import Logo from '@/components/ui/logo';
import MeetingHistory from '@/components/MeetingHistory';
import { DeviceSettingsModal } from '@/components/DeviceSettingsModal';
import { generateRoomId } from '@/lib/client-utils';
import { useMeetingHistory } from '@/hooks/useMeetingHistory';
import { Loader2, Video, Plus, Calendar, Settings } from 'lucide-react';
import { useRouter } from 'next/navigation';
import React, { useState, useEffect } from 'react';

export default function Page() {
  const [room, setRoom] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [placeholder, setPlaceholder] = useState('');
  const [settingsOpen, setSettingsOpen] = useState(false);
  const router = useRouter();
  const { createMeetingFromName } = useMeetingHistory();

  useEffect(() => {
    const meetingExamples = [
      'Reunião de Equipe',
      'Daily Standup',
      'Apresentação Cliente',
      'Workshop Design',
      'Brainstorm Projeto',
      'Review Sprint',
      'Onboarding Novatos',
      'Retrospectiva',
      'Planning Poker',
      'Demo Day',
      'Code Review',
      'Sync Semanal',
    ];

    let currentExampleIndex = 0;

    const startTypingAnimation = () => {
      const text = meetingExamples[currentExampleIndex];
      let charIndex = 0;
      setPlaceholder('');

      const typeEffect = () => {
        if (charIndex < text.length) {
          setPlaceholder(text.substring(0, charIndex + 1));
          charIndex++;
          setTimeout(typeEffect, 100);
        } else {
          // Após completar a digitação, aguarda antes do próximo exemplo
          setTimeout(() => {
            currentExampleIndex = (currentExampleIndex + 1) % meetingExamples.length;
            startTypingAnimation();
          }, 3000);
        }
      };

      typeEffect();
    };

    // Inicia a primeira animação após um pequeno delay
    setTimeout(startTypingAnimation, 500);
  }, []);

  const startMeeting = async () => {
    setIsLoading(true);

    try {
      // Se não há nome digitado, gera um nome único
      const roomName = room.trim() || generateRoomId();

      // Cria uma URL amigável substituindo espaços e caracteres especiais
      const cleanRoomName = roomName
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '') // Remove acentos
        .replace(/[^a-z0-9\s-]/g, '') // Remove caracteres especiais
        .replace(/\s+/g, '-') // Substitui espaços por hífens
        .replace(/-+/g, '-') // Remove hífens duplos
        .replace(/^-|-$/g, ''); // Remove hífens no início/fim

      // Armazena o nome original no sessionStorage para exibir corretamente
      sessionStorage.setItem('originalRoomName', roomName);

      // Cria entrada no histórico de reuniões
      const meeting = createMeetingFromName(roomName);
      sessionStorage.setItem('currentMeetingId', meeting.id);

      // Armazena os links para compartilhamento
      const hostUrl = `${window.location.origin}/${cleanRoomName}?host=true`;
      const guestUrl = `${window.location.origin}/${cleanRoomName}`;

      sessionStorage.setItem('hostUrl', hostUrl);
      sessionStorage.setItem('guestUrl', guestUrl);

      // Entra como anfitrião
      router.push(`/${cleanRoomName}?host=true`);
    } catch (error) {
      console.error('Erro ao iniciar reunião:', error);
      setIsLoading(false);
    }
  };

  const generateRandomRoom = () => {
    const randomRoom = generateRoomId();
    setRoom(randomRoom);
  };

  // Handle Enter key press in input
  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && !isLoading) {
      event.preventDefault();
      startMeeting();
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header estilo Jitsi */}
      <header className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <Logo className="w-32 h-auto" />
            </div>
            <div className="flex items-center space-x-4">
              <DeviceSettingsModal
                open={settingsOpen}
                onOpenChange={setSettingsOpen}
                trigger={
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-gray-600 hover:text-gray-900 hover:bg-gray-100"
                    onClick={() => setSettingsOpen(true)}
                  >
                    <Settings className="w-4 h-4 mr-2" />
                    Configurações
                  </Button>
                }
              />
            </div>
          </div>
        </div>
      </header>

      {/* Seção principal com gradiente baseado no logo */}
      <div className="relative bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900 py-20">
        {/* Gradiente adicional para criar profundidade */}
        <div className="absolute inset-0 bg-gradient-to-r from-slate-800/30 via-transparent to-blue-800/30" />

        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">Evolu Meet</h1>
          <p className="text-xl text-blue-100 mb-8">
            <strong>Reuniões com segurança e alta qualidade</strong>
          </p>

          {/* Formulário de entrada integrado */}
          <div className="max-w-lg mx-auto">
            <div className="flex bg-white rounded-lg overflow-hidden shadow-lg mb-4">
              <input
                className="flex-1 border-none bg-white text-gray-900 placeholder:text-gray-500 focus:ring-0 focus:outline-none px-4 py-4 text-lg shadow-none"
                type="text"
                placeholder={placeholder || 'Digite o nome da sala...'}
                value={room}
                onChange={(e) => setRoom(e.target.value)}
                onKeyDown={handleKeyDown}
                disabled={isLoading}
              />
              <Button
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-4 font-semibold border-0 rounded-none"
                onClick={startMeeting}
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Iniciando...
                  </>
                ) : (
                  <>Iniciar reunião</>
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Seção do histórico de reuniões */}
      <div className="bg-white py-12 flex-grow">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <MeetingHistory />
        </div>
      </div>

      {/* Seção Jitsi as a Service */}
      <div className="relative bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900 py-12">
        {/* Gradiente adicional para criar profundidade */}
        <div className="absolute inset-0 bg-gradient-to-r from-slate-800/30 via-transparent to-blue-800/30" />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl font-bold text-white mb-4">
            Transforme suas reuniões com o poder da Inteligência Artificial.
          </h2>
          <p className="text-gray-300 max-w-2xl mx-auto">
            Automatize a transcrição, crie resumos acionáveis e garanta que nenhuma informação
            importante seja perdida. Libere sua equipe para focar no que realmente importa: inovar e
            tomar decisões estratégicas.
          </p>
        </div>
      </div>
    </div>
  );
}
